/**
 * Toast Provider Component
 * Feature: 034-global-error-handling
 * Constitution: v1.11.0 - Principle XI (Web Application Architecture)
 * 
 * @description Wrapper component for react-hot-toast with custom configuration
 * @implements FR-005, FR-006, FR-008
 * @author Peter Dobler
 * @created 2026-01-20
 */

import React, { ReactNode } from 'react';
import { Toaster, ToasterProps } from 'react-hot-toast';

/**
 * Props for ToastProvider component
 */
export interface ToastProviderProps {
  /** Child components to render */
  children: ReactNode;
  /** Override default toast position */
  position?: ToasterProps['position'];
}

/**
 * Default toast configuration
 * FR-006: Auto-dismiss durations
 * FR-008: Accessibility (ARIA live region)
 */
const defaultToasterProps: ToasterProps = {
  position: 'bottom-right',
  gutter: 8,
  containerStyle: {
    bottom: 24,
    right: 24,
  },
  toastOptions: {
    // Default styling
    style: {
      background: '#fff',
      color: '#374151',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      borderRadius: '0.5rem',
      padding: '0.75rem 1rem',
      maxWidth: '400px',
    },
    // FR-006: Auto-dismiss durations
    duration: 5000, // Default 5 seconds
    success: {
      duration: 3000, // Success: 3 seconds
      iconTheme: {
        primary: '#10b981',
        secondary: '#fff',
      },
      style: {
        borderLeft: '4px solid #10b981',
      },
    },
    error: {
      duration: Infinity, // Errors persist until dismissed
      iconTheme: {
        primary: '#ef4444',
        secondary: '#fff',
      },
      style: {
        borderLeft: '4px solid #ef4444',
      },
    },
    // Custom loading styling
    loading: {
      iconTheme: {
        primary: '#6366f1',
        secondary: '#fff',
      },
      style: {
        borderLeft: '4px solid #6366f1',
      },
    },
  },
};

/**
 * Toast Provider Component
 * Wraps application with react-hot-toast Toaster
 * 
 * @example
 * ```tsx
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 * ```
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position,
}) => {
  const toasterProps: ToasterProps = {
    ...defaultToasterProps,
    position: position ?? defaultToasterProps.position,
  };

  return (
    <>
      {children}
      {/* FR-008: ARIA live region for screen readers */}
      <Toaster
        {...toasterProps}
      />
    </>
  );
};

export default ToastProvider;
